import { IEvent, IProgram } from "../interfaces/models/IEvent";
import { getEventById, updateEvent } from "../Services/Event";
import { sortProgramsBySchedule } from "./groupProgramsByDay";

export const programIdentityKey = (p: IProgram): string =>
  `${p.title}|${p.startTime}|${p.endTime}`;

const allProgramsPresent = (
  expected: IProgram[],
  actual: IProgram[],
): boolean => {
  const actualKeys = new Set(actual.map(programIdentityKey));
  return expected.every((p) => actualKeys.has(programIdentityKey(p)));
};

type ProgramMutation = (currentPrograms: IProgram[]) => IProgram[];

/**
 * Applies a program mutation using the latest event from the server.
 * Retries when a concurrent edit causes the saved program list to be incomplete.
 */
export async function updateEventPrograms(
  eventId: string,
  mutatePrograms: ProgramMutation,
  maxRetries = 3,
): Promise<IEvent> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const latestEvent = await getEventById(eventId);
    if (!latestEvent) {
      throw new Error("Failed to load event");
    }

    const updatedPrograms = sortProgramsBySchedule(
      mutatePrograms(latestEvent.program || []),
    );
    const updatedEvent: IEvent = {
      ...latestEvent,
      program: updatedPrograms,
    };

    await updateEvent(updatedEvent);

    const reloadedEvent = await getEventById(eventId);
    if (!reloadedEvent) {
      throw new Error("Failed to reload event");
    }

    if (allProgramsPresent(updatedPrograms, reloadedEvent.program || [])) {
      return reloadedEvent;
    }
  }

  throw new Error(
    "Failed to save program changes due to a concurrent update. Please try again.",
  );
}
